import React, { useState } from 'react';
import { Box, Paper, TextField, Button, Typography, Alert, CircularProgress, Link, IconButton, InputAdornment } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { resetPassword } from '../services/api';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const validationSchema = Yup.object({
  password: Yup.string()
    .min(6, 'La contraseña debe tener al menos 6 caracteres')
    .required('La contraseña es obligatoria'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Las contraseñas no coinciden')
    .required('Confirma tu nueva contraseña'),
});

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

// Nota: no validamos el token con una llamada aparte antes de mostrar el
// formulario (la versión vieja lo hacía contra un endpoint que nunca
// existió). El backend valida token + expiración en el mismo POST de
// /auth/reset-password, así que solo hay una llamada real, no dos.
const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const token = query.get('token');

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        formik.setSubmitting(true);
        setErrorMessage('');
        await resetPassword(token, values.password);
        setSuccessMessage('Contraseña actualizada. Ya puedes iniciar sesión con tu nueva contraseña.');
        setTimeout(() => navigate('/login'), 4000);
      } catch (error) {
        setErrorMessage(
          error.response?.data?.detail ||
            'No se pudo restablecer la contraseña. El enlace puede haber expirado.'
        );
      } finally {
        formik.setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: '400px',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Nueva contraseña
        </Typography>

        {!token ? (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              Este enlace no es válido. Solicita uno nuevo.
            </Alert>
            <Box sx={{ textAlign: 'center' }}>
              <Button variant="contained" color="primary" onClick={() => navigate('/forgot-password')}>
                Solicitar nuevo enlace
              </Button>
            </Box>
          </>
        ) : successMessage ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
            <Typography variant="body2" sx={{ mt: 1 }}>
              Redirigiendo al login...
            </Typography>
          </Alert>
        ) : (
          <>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errorMessage}
              </Alert>
            )}
            <form onSubmit={formik.handleSubmit}>
              <TextField
                fullWidth
                id="password"
                name="password"
                label="Nueva contraseña"
                type={showPassword ? 'text' : 'password'}
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="mostrar/ocultar contraseña"
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                id="confirmPassword"
                name="confirmPassword"
                label="Confirmar nueva contraseña"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="mostrar/ocultar confirmación de contraseña"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                disabled={formik.isSubmitting}
                sx={{ mt: 3 }}
              >
                {formik.isSubmitting ? (
                  <>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    Actualizando...
                  </>
                ) : (
                  'Restablecer contraseña'
                )}
              </Button>
            </form>
          </>
        )}

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link href="/login" variant="body2">
            Volver a iniciar sesión
          </Link>
        </Box>
      </Paper>
    </Box>
  );
};

export default ResetPasswordForm;
